/**
 * Property availability is created as an AVAILABILITY diary entry (the booking
 * service projects blocks from it). See bridge-atlas docs/booking-service-architecture.md.
 */

export type RecurrenceFreq = "NONE" | "DAILY" | "WEEKLY";

/** Weekday toggle options for "repeat on" (Mon-first, UK convention). */
export const WEEKDAY_OPTIONS: { code: string; label: string }[] = [
  { code: "MON", label: "Mon" },
  { code: "TUE", label: "Tue" },
  { code: "WED", label: "Wed" },
  { code: "THU", label: "Thu" },
  { code: "FRI", label: "Fri" },
  { code: "SAT", label: "Sat" },
  { code: "SUN", label: "Sun" },
];

/** Mon–Fri preset (skip weekends). */
export const WEEKDAYS_MON_FRI = ["MON", "TUE", "WED", "THU", "FRI"];

/** Maps JS {@link Date.getDay} (0=Sun) to the weekday code. */
export const WEEKDAY_CODE_BY_INDEX = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export interface AvailabilityUserRef {
  id: string;
  sourceCrm: string;
  externalId: string;
  name?: string;
  email?: string;
}

/** POST /api/ui/v1/diary-entries body for an availability window. */
export interface AvailabilityCreate {
  sourceCrm: string;
  legalEntityId: string;
  header: null;
  bridgeBranchId: null;
  branchId?: string[];
  instanceId: string;
  /** Always GENERAL; the availability marker is {@link entryType}. */
  type: string;
  entryType: "AVAILABILITY";
  title: string;
  description: null;
  startDate: string;
  endDate: string;
  status: string;
  isCancelled: boolean;
  recurring: boolean;
  recurrenceType?: string | null;
  recurrenceInterval?: number | null;
  recurrenceUntil?: string | null;
  /** Occurrence dates (yyyy-MM-dd) to skip. */
  recurrenceExceptions?: string[] | null;
  /** Weekdays a WEEKLY recurrence repeats on (MON..SUN); empty = the start day's weekday. */
  recurrenceDaysOfWeek?: string[] | null;
  /** Ordered priority list (index 0 = priority 1). */
  users: AvailabilityUserRef[];
  properties: AvailabilityUserRef[];
}

/** Per-property slot rules (booking-service-architecture.md §3.2). */
export interface SlotSettings {
  /** Numeric fields are null while the input is cleared in the settings form. */
  slotDuration: number | null;
  slotInterval: number | null;
  topupTravelTime: number | null;
  leadsPerSlot: number | null;
  generationHorizonDays: number | null;
  /** Minimum lead time, in whole hours, between now and the first bookable slot. */
  minNoticeHours: number | null;
  /**
   * Spread bookings round-robin (least-recently-assigned first) across the assigned
   * negotiators. Off — the default, and what an absent value means — hands every slot to
   * the highest-priority negotiator who is free.
   */
  roundRobinEnabled?: boolean;
  timezone: string;
  /**
   * Default availability window — used by the atlas booking service when a property
   * has no AVAILABILITY diary entry yet (auto-provisioning on first slot query).
   * Null/undefined means "use server default": 10:00–17:00, WEEKLY, TUE–FRI.
   */
  /** "HH:MM" 24-hour window start, e.g. "09:00". */
  defaultWindowStart?: string | null;
  /** "HH:MM" 24-hour window end, e.g. "18:00". */
  defaultWindowEnd?: string | null;
  /** Recurrence pattern: "DAILY" or "WEEKLY". */
  defaultRecurrenceType?: "DAILY" | "WEEKLY" | null;
  /** Days of week active when WEEKLY; empty array = none selected; null/undefined = server default (Mon–Fri). */
  defaultRecurrenceDays?: string[] | null;
  /** Ordered priority list of default users (index 0 = priority 1). Live assignments only. */
  users?: AvailabilityUserRef[];
  /**
   * Every assignment including any the nightly CRM roster reconciliation switched off.
   * Present so a negotiator who dropped off the roster is shown as inactive rather than
   * vanishing from the form, which read as the settings changing by themselves.
   */
  assignments?: SlotSettingsAssignment[];
  activeFlag?: boolean;
  /**
   * Which tier these settings were inherited from. Only present on the `/effective` response —
   * the plain scope GET reports a single scope, so there is nothing to attribute.
   */
  resolvedScope?: "PROPERTY" | "BRANCH" | "LEGAL_ENTITY" | null;
  /** Id of the property / branch / legal entity the settings are attached to. */
  resolvedFromId?: string | null;
}

/** IANA timezone options for the slot-settings dropdown (full list when the runtime supports it). */
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = (() => {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
    ).supportedValuesOf?.("timeZone");
    if (supported && supported.length) return supported.map((z) => ({ value: z, label: z }));
  } catch {
    /* fall back to a curated list below */
  }
  return [
    "Europe/London",
    "Europe/Dublin",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Madrid",
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Los_Angeles",
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Singapore",
    "Australia/Sydney",
  ].map((z) => ({ value: z, label: z }));
})();

/** Effective default availability window applied when the server has none saved. */
/** The agency estate is UK-based, so this is right far more often than not. */
export const DEFAULT_TIMEZONE = "Europe/London";

export const DEFAULT_WINDOW_START = "10:00";
export const DEFAULT_WINDOW_END = "17:00";

/**
 * The house rule set — what an agency configures when it has no reason to
 * configure anything else: quarter-hour viewings running back-to-back
 * (interval 0) through a 10:00–17:00 weekday window, a fortnight ahead, with a
 * day's notice. The "Use recommended defaults" button writes exactly this, and
 * {@link DEFAULT_SLOT_SETTINGS} falls back to the same numbers, so the values
 * the form suggests and the values it assumes are never two different sets.
 */
export const RECOMMENDED_SLOT_SETTINGS: SlotSettings = {
  slotDuration: 15,
  /** 0 = back-to-back; the next viewing starts as the previous one ends. */
  slotInterval: 0,
  topupTravelTime: 0,
  leadsPerSlot: 1,
  generationHorizonDays: 14,
  minNoticeHours: 24,
  // Opt-in, so the recommended set leaves it off — matching the server default.
  roundRobinEnabled: false,
  timezone: DEFAULT_TIMEZONE,
  defaultWindowStart: DEFAULT_WINDOW_START,
  defaultWindowEnd: DEFAULT_WINDOW_END,
  defaultRecurrenceType: "WEEKLY",
  defaultRecurrenceDays: WEEKDAYS_MON_FRI,
};

/** One negotiator attached to a slot-settings row, with whether the assignment is still live. */
export interface SlotSettingsAssignment {
  userId: string;
  externalId?: string | null;
  sourceCrm?: string | null;
  name?: string | null;
  priority?: number | null;
  /** False once the roster reconciliation switched this assignment off. */
  activeFlag?: boolean | null;
  /** When it was switched off; null while active. */
  deactivatedAt?: string | null;
  /** What switched it off, e.g. "job:user-reconciliation". */
  deactivatedBy?: string | null;
}

export const DEFAULT_SLOT_SETTINGS: SlotSettings = {
  ...RECOMMENDED_SLOT_SETTINGS,
  // The recurrence half is a suggestion for an empty form, not an assumption to
  // make about a payload that didn't carry one — `resolveSlotSettings` normalises
  // a missing recurrence to null, and this keeps that reading honest.
  defaultRecurrenceType: null,
  defaultRecurrenceDays: null,
};

/**
 * Every field blank. Used when `/booking/slot-settings` resolves nothing for the
 * requested scope — the form then starts empty rather than showing the system
 * defaults, which would read as saved values that were never persisted.
 */
export const EMPTY_SLOT_SETTINGS: SlotSettings = {
  slotDuration: null,
  slotInterval: null,
  topupTravelTime: null,
  leadsPerSlot: null,
  generationHorizonDays: null,
  minNoticeHours: null,
  timezone: "",
  defaultWindowStart: null,
  defaultWindowEnd: null,
  defaultRecurrenceType: null,
  defaultRecurrenceDays: null,
  users: [],
};

/**
 * What a form with nothing saved against it opens on.
 *
 * Distinct from {@link EMPTY_SLOT_SETTINGS}, which stays all-null because it is
 * the yardstick `isEmptySlotSettings` measures a *server payload* against. These
 * are the two settings an operator would set to the same value every time, so
 * the form pre-fills them rather than making that a chore.
 */
export const NEW_SLOT_SETTINGS: SlotSettings = {
  ...EMPTY_SLOT_SETTINGS,
  timezone: DEFAULT_TIMEZONE,
  defaultRecurrenceType: "WEEKLY",
};

/** Fields that carry an actual setting — scope ids and `activeFlag` don't count. */
const SLOT_SETTING_FIELDS = [
  "slotDuration",
  "slotInterval",
  "topupTravelTime",
  "leadsPerSlot",
  "generationHorizonDays",
  "minNoticeHours",
  "timezone",
  "defaultWindowStart",
  "defaultWindowEnd",
  "defaultRecurrenceType",
  "defaultRecurrenceDays",
] as const satisfies readonly (keyof SlotSettings)[];

/** True when the payload carries no settings at all (missing body, `{}`, all nulls). */
export function isEmptySlotSettings(s: Partial<SlotSettings> | null | undefined): boolean {
  const src = s ?? {};
  if (src.users?.length) return false;
  return SLOT_SETTING_FIELDS.every((key) => src[key] == null);
}

/**
 * Merges server-returned slot settings over the system defaults. The backend
 * returns `null` window bounds when none are saved; a plain spread would put
 * that null into state, so the panel would show the 10:00–17:00 fallback while
 * saving empty. Coalescing the window bounds keeps what's shown and what's
 * persisted in sync.
 */
export function resolveSlotSettings(s: Partial<SlotSettings> | null | undefined): SlotSettings {
  const src = s ?? {};
  return {
    ...DEFAULT_SLOT_SETTINGS,
    ...src,
    defaultWindowStart: src.defaultWindowStart ?? DEFAULT_WINDOW_START,
    defaultWindowEnd: src.defaultWindowEnd ?? DEFAULT_WINDOW_END,
    defaultRecurrenceType: normalizeRecurrenceType(src.defaultRecurrenceType),
  };
}

/**
 * The API isn't consistent about the casing of recurrence values (the diary
 * code upper-cases them on the way in for the same reason), and the Repeat
 * buttons match on the exact literal — so normalise once, here, rather than
 * having each consumer guess. Anything unrecognised becomes null, which the
 * form treats as "not set".
 */
function normalizeRecurrenceType(value: unknown): "DAILY" | "WEEKLY" | null {
  const normalized = typeof value === "string" ? value.trim().toUpperCase() : "";
  return normalized === "DAILY" || normalized === "WEEKLY" ? normalized : null;
}
